// Image caching utility using localStorage
const IMAGE_CACHE_KEY = 'riftbound_image_cache';
const MAX_CACHE_SIZE = 50; // Maximum number of images to cache
const MAX_IMAGE_SIZE = 1024 * 1024; // 1MB max size to cache (to avoid localStorage overflow)

// Load the image cache from localStorage
const loadImageCache = () => {
  try {
    const cached = localStorage.getItem(IMAGE_CACHE_KEY);
    return cached ? JSON.parse(cached) : {};
  } catch (e) {
    console.warn('Failed to load image cache:', e);
    return {};
  }
};

// Save the image cache to localStorage
const saveImageCache = (cache) => {
  try {
    // Keep only the most recent MAX_CACHE_SIZE entries
    const entries = Object.entries(cache);
    if (entries.length > MAX_CACHE_SIZE) {
      // Sort by timestamp (newest first) and keep only the first MAX_CACHE_SIZE entries
      entries.sort((a, b) => b[1].timestamp - a[1].timestamp);
      const trimmedEntries = entries.slice(0, MAX_CACHE_SIZE);
      cache = Object.fromEntries(trimmedEntries);
    }

    localStorage.setItem(IMAGE_CACHE_KEY, JSON.stringify(cache));
  } catch (e) {
    console.warn('Failed to save image cache:', e);
  }
};

// Get an image from cache
export const getCachedImage = (url) => {
  const cache = loadImageCache();
  const cachedImage = cache[url];

  if (cachedImage) {
    // Check if the cached image is still valid (not expired)
    const now = Date.now();
    const cacheDuration = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

    if (now - cachedImage.timestamp < cacheDuration) {
      return cachedImage.data;
    } else {
      // Remove expired entry
      delete cache[url];
      saveImageCache(cache);
    }
  }

  return null;
};

// Set an image in cache
export const setCachedImage = (url, imageData) => {
  // Don't cache if the image data is too large (to prevent localStorage overflow)
  if (imageData && typeof imageData === 'string' && imageData.length > MAX_IMAGE_SIZE) {
    return; // Don't cache large images
  }

  const cache = loadImageCache();
  cache[url] = {
    data: imageData,
    timestamp: Date.now()
  };
  saveImageCache(cache);
};

// Clear the image cache
export const clearImageCache = () => {
  localStorage.removeItem(IMAGE_CACHE_KEY);
};

// Helper function to get the best image URL for a card, prioritizing local images and base cards over alternates
export const getCardImageUrl = (card) => {
  if (!card) return null;

  // Check if this is an alternate art card and skip it if we're in a context that prefers base images
  // This is determined by checking for tags or properties that indicate alternate art
  if (card.is_alternate_art ||
      (card.tags && Array.isArray(card.tags) && card.tags.some(tag =>
        typeof tag === 'string' && tag.toLowerCase().includes('alternate'))) ||
      (card.name && typeof card.name === 'string' && card.name.toLowerCase().includes('alternate')) ||
      (card.variant && typeof card.variant === 'string' && card.variant.toLowerCase().includes('alternate')) ||
      (card.variant_type && typeof card.variant_type === 'string' && card.variant_type.toLowerCase().includes('alternate'))) {
    // If this is an alternate art card, return null to indicate we should skip it
    // This will cause the caller to potentially look for a base version
    return null;
  }

  // Prioritize image_src (local images) first
  if (card.image_src) {
    return card.image_src;
  }

  // Then try image_url (remote images)
  if (card.image_url) {
    return card.image_url;
  }

  // Finally, try other possible image fields
  if (card.image) {
    return card.image;
  }

  // Return null if no image is available
  return null;
};